import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PaymentCallback() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const reference = params.get("reference") || params.get("trxref");
  const flwRef = params.get("tx_ref");
  const flwStatus = params.get("status");
  const [state, setState] = useState({ status: "verifying", data: null, error: "" });

  useEffect(() => {
    if (!reference && !flwRef) {
      setState({ status: "error", data: null, error: "No payment reference found." });
      return;
    }
    (async () => {
      try {
        if (flwRef) {
          const { data } = await api.get(`/flutterwave/verify/${flwRef}`);
          setState({ status: data.status === "success" ? "success" : "failed", data, error: "" });
          return;
        }
        const PENDING = ["pending", "ongoing", "processing", "queued"];
        for (let attempt = 0; attempt < 8; attempt++) {
          const { data } = await api.get(`/payments/verify/${reference}`);
          if (!PENDING.includes(data.status)) { setState({ status: data.status, data, error: "" }); return; }
          setState({ status: "processing", data, error: "" });
          await new Promise((r) => setTimeout(r, 4000));
        }
      } catch (e) {
        setState({
          status: "error", data: null,
          error: formatApiError(e.response?.data?.detail) || e.message,
        });
      }
    })();
  }, [reference, flwRef, flwStatus]);

  const nextHref =
    state.data?.purpose === "product_order"
      ? state.data.detail?.product_id ? `/product/${state.data.detail.product_id}` : "/discover"
      : state.data?.purpose === "roles_addon" ? "/seller/stage" : "/billing";

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md rounded-xl border border-[#27272A] bg-[#121212] p-8 text-center">
        {state.status === "verifying" && (
          <>
            <Loader2 className="h-10 w-10 text-[#007AFF] animate-spin mx-auto" />
            <h1 className="mt-6 font-black text-2xl tracking-tight">Verifying payment…</h1>
            <p className="text-neutral-400 text-sm mt-2">This usually takes a second or two.</p>
            <p className="text-xs text-neutral-500 font-mono mt-4">{reference || flwRef}</p>
          </>
        )}
        {state.status === "processing" && (
          <>
            <Loader2 className="h-10 w-10 text-[#FF9F0A] animate-spin mx-auto" />
            <h1 className="mt-6 font-black text-2xl tracking-tight" data-testid="payment-processing">Payment processing…</h1>
            <p className="text-neutral-400 text-sm mt-2">Your bank is confirming the transfer. We'll activate it automatically the moment Paystack confirms — you can safely close this page.</p>
            <p className="text-xs text-neutral-500 font-mono mt-4">{reference}</p>
          </>
        )}
        {state.status === "success" && (
          <>
            <CheckCircle2 className="h-12 w-12 text-[#34C759] mx-auto" />
            <h1 className="mt-4 font-black text-2xl tracking-tight">Payment successful</h1>
            <p className="text-neutral-400 text-sm mt-2">
              {state.data?.purpose === "subscription"
                ? (state.data?.plan === "annual" || Number(state.data?.amount_usd) >= 1000
                  ? "Your yearly access is active for the next 12 months."
                  : "Your monthly access is active for another 30 days.")
                : state.data?.purpose === "roles_addon"
                  ? "All Stage roles are unlocked for the next 30 days — go host that show."
                  : "Your order has been placed."}
            </p>
            <div className="mt-6 text-sm text-neutral-500 space-y-1 font-mono">
              <div>USD ${state.data?.amount_usd?.toFixed(2)}</div>
              <div className="text-xs">Charged: {state.data?.currency} {state.data?.amount_charged?.toLocaleString()}</div>
            </div>
            <Button
              data-testid="payment-continue"
              className="mt-8 bg-white text-black hover:bg-neutral-200 rounded-full w-full h-11 font-semibold"
              onClick={() => nav(nextHref)}
            >
              Continue
            </Button>
          </>
        )}
        {(state.status === "failed" || state.status === "abandoned") && (
          <>
            <XCircle className="h-12 w-12 text-[#FF3B30] mx-auto" />
            <h1 className="mt-4 font-black text-2xl tracking-tight">
              Payment {state.status}
            </h1>
            <p className="text-neutral-400 text-sm mt-2">
              Nothing was charged. You can safely try again.
            </p>
            <Link to={state.data?.purpose === "product_order" ? "/discover" : "/billing"}>
              <Button className="mt-8 bg-white text-black hover:bg-neutral-200 rounded-full w-full h-11 font-semibold">
                Back
              </Button>
            </Link>
          </>
        )}
        {state.status === "error" && (
          <>
            <XCircle className="h-12 w-12 text-[#FF3B30] mx-auto" />
            <h1 className="mt-4 font-black text-2xl tracking-tight">Verification error</h1>
            <p className="text-neutral-400 text-sm mt-2">{state.error}</p>
            <Link to="/billing">
              <Button className="mt-8 bg-white text-black hover:bg-neutral-200 rounded-full w-full h-11 font-semibold">
                Back to billing
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
